/**
 * Resolve "is this href a widget-internal navigation target?" for
 * links embedded in operator / AI-authored message HTML.
 *
 * The AI assistant sometimes ends replies with a link to a help
 * article (`/help/{folderSlug}/{articleSlug}?locale=en`) under the
 * assumption the visitor is on the operator's site. The widget runs
 * on its own origin (`widget.wexio.com` in production, `localhost:3002`
 * in dev), so following the link with default browser navigation
 * lands on a 404 — the widget app has no `/help/...` page.
 *
 * Parse these patterns here and let the click handler in the message
 * body route them through `setRoute(...)` instead, opening the
 * article in the widget's own viewer.
 *
 * What counts as "internal":
 *
 *   - Same-origin link (the widget's host or `localhost`)
 *   - Path matches `/help/.../<slug>` or `/news/<slug>`
 *
 * Cross-origin URLs are always treated as external — even if the path
 * looks like `/help/...`, the visitor probably wants the operator's
 * marketing site, not the widget's internal article view.
 */

export type InternalLink =
  | { kind: "help-article"; slug: string; locale?: string }
  | { kind: "news-article"; slug: string; locale?: string };

export function parseInternalLink(href: string, base?: string): InternalLink | null {
  if (!href) return null;
  // Skip bare fragments / non-navigational anchors — `<a href="#section">`
  // should scroll to the target inside the bubble, not route the widget.
  if (href.startsWith("#")) return null;
  // Skip non-http(s) protocols (mailto:, tel:, sms:, javascript:, …).
  if (/^[a-z][a-z0-9+\-.]*:/i.test(href) && !/^https?:/i.test(href)) {
    return null;
  }

  // Resolve against the caller-supplied base when present (tests use
  // this to pin a deterministic origin); fall back to the widget's own
  // origin at runtime.
  const resolvedBase =
    base ?? (typeof window !== "undefined" ? window.location.origin : "http://localhost");

  let url: URL;
  try {
    url = new URL(href, resolvedBase);
  } catch {
    return null;
  }

  // Same-origin gate against the SAME base we resolved against — a
  // relative path always passes (URL inherits the base's origin); an
  // absolute external URL doesn't (different origin even though the
  // path looks internal).
  try {
    const baseOrigin = new URL(resolvedBase).origin;
    if (url.origin !== baseOrigin) return null;
  } catch {
    /* malformed base — skip the gate, parse anyway */
  }

  const segments = url.pathname.split("/").filter(Boolean);
  if (segments.length < 2) return null;

  const [section, ...rest] = segments;
  // Slug is the FULL path after the section, joined back with `/`.
  // Help articles are keyed by slugs that may themselves contain
  // slashes (`webhooks/history` is one slug, not "folder webhooks +
  // article history"). The BE looks the article up by exactly this
  // string — losing the prefix segments here would mismatch and
  // return null.
  const slug = rest.join("/");
  if (!slug) return null;

  const locale = url.searchParams.get("locale") ?? undefined;

  if (section === "help") return { kind: "help-article", slug, locale };
  // News articles ship under either `/news/<slug>` (the in-widget
  // route convention) or `/article/<slug>` (the operator's public
  // marketing site convention) — accept both.
  if (section === "news" || section === "article") {
    return { kind: "news-article", slug, locale };
  }
  return null;
}
